import { defineStore } from 'pinia'
import type { CartItemsInterface, CartItemInterface, CartShippingMethods, CartExpiration } from '~/types/cartTypes'
import type { LocalDeliveryCourierInterface } from '~/types/market/productBackendTypes'
import isValidResponse from '~/utils/isValidResponse'
import { useLanguageStore } from '~/store/language'
import type { CartFetchResponseInterface } from '~/types/cart/cartBackendTypes'
import * as Sentry from '@sentry/vue'
import { fetchRetryParameters, requestHostHeaders } from '~/composables/constants/api'
import type { RequestHostHeadersType } from '~/composables/constants/api'

function getInitialState() {
  return {
    items: [],
    delivery: null,
    shipping: null,
    has_error: false,
    loaded: false,
    shipping_method: 'air',
    reservation_error: null,
  }
}

export const useCartStore = defineStore('cart', {
  state: (): CartItemsInterface => {
    return getInitialState()
  },
  getters: {
    totalItems(state): number {
      return state.items.length
    },
    totalQuantity(state): number {
      let total = 0

      for (const property in state.items) {
        total += state.items[property].quantity
      }

      return total
    },
    totalSum(state): number {
      let sum = 0
      for (const i in state.items) {
        sum += state.items[i].price * state.items[i].quantity
      }

      return sum
    },
    isLoaded(state): boolean {
      return state.loaded
    },
  },
  actions: {
    add(item: CartItemInterface) {
      this.items.push(item)
    },
    remove(idx: number) {
      const item = this.items[idx]
      if (item) {
        this.items.splice(idx, 1)
      }
    },
    setItems(items: CartItemInterface[]) {
      this.items = items
    },
    setDelivery(delivery: LocalDeliveryCourierInterface[] | null) {
      this.delivery = delivery
    },
    setShipping(shipping: CartShippingMethods | null) {
      this.shipping = shipping
    },
    setExpiration(expiration: CartExpiration | null) {
      this.expiration = expiration
    },
    setShippingMethod(value: string | null) {
      this.shipping_method = value
    },
    shippingDiscountsPercentage() {
      return {
        air: Math.round((this.shipping?.shipping_discounts?.air / this.shipping?.shipping_methods?.air) * 100),
        sea: Math.round((this.shipping?.shipping_discounts?.sea / this.shipping?.shipping_methods?.sea) * 100),
      }
    },
    quantityRemove(idx: number) {
      const item = this.items[idx]
      if (item) {
        item.quantity = Math.max((item.quantity -= 1), 1)
      }
    },
    quantityAdd(idx: number) {
      const item = this.items[idx]
      if (item) {
        item.quantity = Math.min((item.quantity += 1), item.max_quantity)
      }
    },
    setError(has_error: boolean) {
      this.has_error = has_error
    },
    setReservationError(error: string) {
      this.reservation_error = error
    },
    setLoaded(value: boolean) {
      this.loaded = value
    },
    async fetch() {
      this.setError(false)

      const formData = {
        language_id: useLanguageStore().getLanguageId,
      }

      const response = await $fetch<CartFetchResponseInterface>('/api/cart/fetch', {
        method: 'POST',
        body: formData,
        // @ts-ignore
        headers: useRequestHeaders<RequestHostHeadersType>(requestHostHeaders),
        ...fetchRetryParameters,
        retry: 2,
      }).catch((error) => {
        Sentry.captureException(error, (scope) => {
          scope.setContext('Server Response', error.data)

          return scope
        })

        return { status: false, cart: null }
      })

      this.setItems([])
      this.setDelivery(null)
      this.setShipping(null)

      let loaded = true
      if (!isValidResponse(response)) {
        this.setError(true)
        loaded = false
      } else if (response.cart?.items) {
        this.setItems(response.cart.items)
        this.setDelivery(response.cart?.delivery)
        this.setShipping(response.cart?.shipping)
        this.setExpiration(response.cart?.expiration)
      }

      this.setLoaded(loaded)

      return true
    },
    empty() {
      this.items = []
    },
    invalidateCart() {
      this.$reset()
    },
  },
})
